import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import './CitySelector.css';

interface City {
  id: string;
  name_uk: string;
  name_en: string;
  region: string;
  latitude: number;
  longitude: number;
  is_popular: boolean;
}

interface CitySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onCitySelect?: (city: City) => void;
}

export const CitySelector: React.FC<CitySelectorProps> = ({
  isOpen,
  onClose,
  onCitySelect,
}) => {
  const { language, t } = useLanguage();
  const {
    cities,
    currentCity,
    setCurrentCity,
    detectByGeolocation,
    searchCities,
    isLoading,
    error,
    loadCities,
  } = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<City[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load cities on mount
  useEffect(() => {
    if (!cities) {
      loadCities();
    }
  }, [cities, loadCities]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Search cities with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchCities(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchCities]);

  const handleCitySelect = async (city: City) => {
    try {
      // Use the appropriate name based on current language
      const cityName = language === 'en' ? city.name_en : city.name_uk;
      await setCurrentCity(cityName);
      onCitySelect?.(city);
      onClose();
    } catch (err) {
      console.error('Failed to set city:', err);
    }
  };

  const handleDetectLocation = async () => {
    setIsDetecting(true);
    try {
      const detectedCity = await detectByGeolocation();
      if (detectedCity) {
        onClose();
      }
    } catch (err) {
      console.error('Geolocation failed:', err);
    } finally {
      setIsDetecting(false);
    }
  };

  const filteredCities = searchQuery
    ? searchResults
    : cities
    ? [...cities.popular, ...cities.all.filter(city => !city.is_popular)]
    : [];

  // Helper function to check if city is selected (compare both language versions)
  const isCitySelected = (city: City) => {
    return city.name_uk === currentCity || city.name_en === currentCity;
  };

  if (!isOpen) return null;

  return (
    <div className="city-selector-overlay">
      <div className="city-selector-modal">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">{t('title', 'citySelector')}</h2>
          <button
            onClick={onClose}
            className="close-button"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Auto-detect button */}
        <div className="detect-section">
          <button
            onClick={handleDetectLocation}
            disabled={isDetecting || isLoading}
            className="detect-button"
          >
            <i className={`bi ${isDetecting ? 'bi-arrow-clockwise spin' : 'bi-geo-alt'}`}></i>
            {isDetecting ? t('detecting', 'citySelector') : t('detectLocation', 'citySelector')}
          </button>
          {error && (
            <p className="error-message">{error}</p>
          )}
        </div>

        {/* Search */}
        <div className="search-section">
          <div className="search-container">
            <i className="bi bi-search search-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('searchPlaceholder', 'citySelector')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {isSearching && (
              <div className="search-spinner">
                <i className="bi bi-arrow-clockwise spin"></i>
              </div>
            )}
          </div>
        </div>

        {/* Cities list */}
        <div className="cities-list">
          {!cities && isLoading ? (
            <div className="loading-container">
              <i className="bi bi-arrow-clockwise spin loading-icon"></i>
            </div>
          ) : filteredCities.length === 0 ? (
            <div className="no-results">
              {searchQuery ? t('noResults', 'citySelector') : t('loading', 'citySelector')}
            </div>
          ) : (
            <div>
              {/* Popular cities (only when not searching) */}
              {!searchQuery && cities?.popular && cities.popular.length > 0 && (
                <div>
                  <div className="section-header">
                    <h3>{t('popularCities', 'citySelector')}</h3>
                  </div>
                  {cities.popular.map((city) => (
                    <CityItem
                      key={city.id}
                      city={city}
                      isSelected={isCitySelected(city)}
                      onClick={() => handleCitySelect(city)}
                    />
                  ))}
                </div>
              )}

              {/* All cities */}
              {!searchQuery && cities?.all && (
                <div>
                  <div className="section-header">
                    <h3>{t('allCities', 'citySelector')}</h3>
                  </div>
                  {cities.all
                    .filter(city => !city.is_popular)
                    .map((city) => (
                      <CityItem
                        key={city.id}
                        city={city}
                        isSelected={isCitySelected(city)}
                        onClick={() => handleCitySelect(city)}
                      />
                    ))}
                </div>
              )}

              {/* Search results */}
              {searchQuery && searchResults.map((city) => (
                <CityItem
                  key={city.id}
                  city={city}
                  isSelected={isCitySelected(city)}
                  onClick={() => handleCitySelect(city)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface CityItemProps {
  city: City;
  isSelected: boolean;
  onClick: () => void;
}

const CityItem: React.FC<CityItemProps> = ({ city, isSelected, onClick }) => {
  const { language } = useLanguage();
  
  return (
    <button
      onClick={onClick}
      className={`city-item ${isSelected ? 'selected' : ''}`}
    >
      <div className="city-content">
        <i className={`bi bi-geo-alt city-icon ${isSelected ? 'selected' : ''}`}></i>
        <div className="city-info">
          <div className="city-name">
            {language === 'en' ? city.name_en : city.name_uk}
          </div>
          <div className="city-region">{city.region}</div>
        </div>
        {/* {city.is_popular && (
          <span className="popular-badge">
            Популярне
          </span>
        )} */}
      </div>
    </button>
  );
};